import { type FC, useCallback, useRef, useState } from 'react';

import { AccommodationDescriptionWrapper } from '@vakantiesnl/components/src/molecules/AccommodationDescription';
import { Toggle } from '@vakantiesnl/components/src/molecules/Toggle';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { type Search } from '@vakantiesnl/types';

import { useCustomStyles } from './AccommodationDescriptionSection.style';

interface Props {
	/** The accommodation for which the description belongs to. */
	accommodation: Search.FullAccommodationItem;
	/** Whether to show the button for scroll or not, and show the full description instead. */
	withScrollButton?: boolean;
}

export const AccommodationDescriptionSection: FC<Props> = ({ accommodation }) => {
	const microCopies = useMicroCopyContext();
	const { classes, cx } = useCustomStyles();

	const [showMore, setShowMore] = useState(false);
	const descriptionRef = useRef<HTMLDivElement>(null);

	/** On click track the read more info button */
	const trackOnClickShowMore = useCallback(
		(showMore: boolean) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/accommodationPage').then(({ trackClickShowMoreDescription }) => {
					track(
						trackClickShowMoreDescription(
							accommodation,
							showMore ? microCopies['filters.showLess'] : microCopies['filters.showMore'],
						),
					);
				}),
			);
		},
		[accommodation, microCopies],
	);

	const handleToggle = useCallback(() => {
		trackOnClickShowMore(!showMore);
		setShowMore(!showMore);
	}, [showMore, trackOnClickShowMore]);

	// if there is no description object - render nothing
	if (!accommodation.description) return null;

	return (
		<div className={classes.descriptionWrapperWrapperContainer}>
			<div
				id="accommodation-description"
				className={cx(classes.collapsedDescription, { [classes.toggled]: showMore })}
			>
				<AccommodationDescriptionWrapper accommodation={accommodation} ref={descriptionRef} />
			</div>

			{descriptionRef?.current?.scrollHeight && descriptionRef?.current?.scrollHeight > 200 && (
				<div>
					<Toggle
						controls="accommodation-description"
						isOpen={showMore}
						onClick={handleToggle}
						showMoreText={microCopies['filters.showMore']}
						showLessText={microCopies['filters.showLess']}
						buttonVariant="secondary"
						buttonSize="small"
					/>
				</div>
			)}
		</div>
	);
};
